import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { Select } from "../../components/ui/form/Select";
import { api } from "../../lib/api";
import { Modal } from "../albums/Modal";
import { rooms, type Room } from "./api";
import { OverlayDialog } from "./OverlayDialog";

/**
 * How the room behaves, in one place you open on purpose.
 *
 * ## Why these left the deck column
 *
 * They used to sit under the transport, in the column that also held the
 * artwork and the play button - a column doing three unrelated jobs: showing
 * what is playing, controlling it, and configuring the room. The third is the
 * odd one out. A host decides whether they accept explicit tracks once, and
 * then never touches it again for the rest of the set.
 *
 * Permanent screen space for a decision made once is space taken from the
 * queue, which is the thing a host actually watches. So they moved here, and
 * the column they were in went to the studio.
 *
 * ## Everything here saves on change
 *
 * There is no Save button and there should not be. Each control is a single
 * field with its own request, the room is live while they are being changed,
 * and a dialog that batched them would leave a host wondering whether their
 * change had taken effect while people were listening.
 */
export function RoomSettingsDialog({
  room,
  onChange,
  onClose,
}: {
  room: Room;
  onChange: () => void;
  onClose: () => void;
}) {
  return (
    <Modal title="Room settings" onClose={onClose} wide>
      <div className="flex flex-col gap-8" data-room-settings>
        {/* First, because it is the only setting here that changes what the
            room is called rather than how it behaves. */}
        <NameSetting room={room} onChange={onChange} />
        <ExplicitSetting room={room} onChange={onChange} />
        <AutoPlaySetting room={room} onChange={onChange} />
        <ShowQueueSetting room={room} onChange={onChange} />
        <FairnessSettings room={room} onChange={onChange} />
        <RefillSetting room={room} onChange={onChange} />

        {/* The viewer this token was generated for now exists. */}
        {room.overlay_token && <OverlayLink token={room.overlay_token} />}
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * What this set is called.
 *
 * ## Everything but the field already existed
 *
 * `stations.name` has been a column since the table was created, the settings
 * endpoint has validated it at eighty characters, the presenter has sent it,
 * and four places render it: the console header, the live room's status line,
 * the on-air rail and the dock. There was simply nowhere to type one, so every
 * room on the site fell back to its host's display name.
 *
 * ## Saved on blur, not on every keystroke
 *
 * A name is a sentence somebody is composing; a request per character would put
 * "F", "Fr", "Fri" through the API and briefly title a live broadcast "F". It
 * commits when the field is left or Enter is pressed, which is also when a
 * person has decided.
 *
 * ## Empty is a name too
 *
 * Clearing it is how you go back to being called by your own name, which is the
 * default and a perfectly good answer - so an empty field stores null rather
 * than being refused. The endpoint has always done this; `trim() ?: null`.
 */
function NameSetting({ room, onChange }: { room: Room; onChange: () => void }) {
  const [name, setName] = useState(room.name ?? '');

  const update = useMutation({
    mutationFn: (next: string) => rooms.settings({ name: next }),
    onSuccess: onChange,
  });

  const commit = () => {
    // Nothing changed, so nothing to send - including the common case of
    // opening the dialog, reading it and clicking away.
    if (name.trim() === (room.name ?? '')) return;

    update.mutate(name.trim());
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-1.5">
        <span className="meta-caps text-ink">Name this set</span>

        <input
          value={name}
          maxLength={80}
          data-room-name
          placeholder={room.host.display_name}
          disabled={update.isPending}
          onChange={(event) => setName(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
          className="w-full rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 text-sm text-ink outline-none transition-colors focus:border-line-strong disabled:opacity-60"
        />
      </label>

      <p className="font-mono text-[11px] leading-relaxed text-soft">
        {name.trim() === ''
          ? `Rooms are called after their host unless you say otherwise — yours reads ${room.host.display_name}.`
          : 'Shown on your room, in the on-air list and on the player bar of everybody listening.'}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Whether a finished track rolls into the next one.
 *
 * On the deck beside the submissions policy, because both are things a host
 * reaches for *during* a broadcast rather than settings they configured once.
 *
 * Off is the reviewing mode: the room stops at the end of each track, the deck
 * keeps it loaded, and the review box stays pointed at it until the host
 * presses play. The consequence is dead air, so the control says so - a host
 * who turns this on absent-mindedly and walks away should have been told.
 */
function AutoPlaySetting({
  room,
  onChange,
}: {
  room: Room;
  onChange: () => void;
}) {
  const update = useMutation({
    mutationFn: (auto_play: boolean) => rooms.settings({ auto_play }),
    onSuccess: onChange,
  });

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <div className="flex items-center justify-between gap-3">
        <span className="meta-caps text-ink">Auto play</span>

        <button
          type="button"
          role="switch"
          aria-checked={room.auto_play}
          aria-label="Auto play"
          data-auto-play
          disabled={update.isPending}
          onClick={() => update.mutate(!room.auto_play)}
          className={`h-6 w-11 shrink-0 rounded-[var(--radius-pill)] border transition-colors ${
            room.auto_play
              ? "border-transparent bg-accent"
              : "border-line bg-raised"
          }`}
        >
          <span
            aria-hidden
            className={`block h-4 w-4 rounded-full bg-ink transition-transform ${
              room.auto_play ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <p className="font-mono text-[11px] leading-relaxed text-soft">
        {room.auto_play
          ? "The next track starts on its own when this one ends."
          : "The room holds at the end of each track. Nothing plays until you press play."}
      </p>
    </div>
  );
}

/**
 * Whether the room shows what is coming up.
 *
 * ## The count stays either way
 *
 * "How many are waiting" is what somebody deciding whether to submit needs, and
 * hiding it would make the submit button a coin toss. This hides the contents:
 * which tracks, in what order, and at what tier. A host running a set where the
 * running order is the show gets to keep it; a host whose boosts are the point
 * gets to publish it.
 *
 * ## Enforced on the server
 *
 * `ShowStationAction` withholds the rows rather than the client hiding them, so
 * turning this off is not one `fetch` away from being undone. This switch is
 * the control, not the mechanism.
 */
function ShowQueueSetting({
  room,
  onChange,
}: {
  room: Room;
  onChange: () => void;
}) {
  const update = useMutation({
    mutationFn: (show_queue: boolean) => rooms.settings({ show_queue }),
    onSuccess: onChange,
  });

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <div className="flex items-center justify-between gap-3">
        <span className="meta-caps text-ink">Show the queue</span>

        <button
          type="button"
          role="switch"
          aria-checked={room.show_queue}
          aria-label="Show the queue"
          data-show-queue
          disabled={update.isPending}
          onClick={() => update.mutate(!room.show_queue)}
          className={`h-6 w-11 shrink-0 rounded-[var(--radius-pill)] border transition-colors ${
            room.show_queue
              ? "border-transparent bg-accent"
              : "border-line bg-raised"
          }`}
        >
          <span
            aria-hidden
            className={`block h-4 w-4 rounded-full bg-ink transition-transform ${
              room.show_queue ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <p className="font-mono text-[11px] leading-relaxed text-soft">
        {room.show_queue
          ? "Listeners can see what is coming up, and which submissions were boosted."
          : "Listeners see how many are waiting, but not what they are. Your console is unchanged."}
      </p>
    </div>
  );
}

/**
 * The way in to the stream graphics.
 *
 * ## A button, not a URL
 *
 * There are three graphics and six appearance options behind this, which is
 * more than a settings panel in a sidebar can hold without becoming the sidebar.
 * It is also a thing a host does once while setting up a scene collection and
 * then never again, which is exactly what a dialog is for.
 *
 * ## And nothing of the token out here
 *
 * The console is a screen hosts screen-share while setting up, so the URL - the
 * only credential the overlay has - never renders until somebody opens the
 * dialog and asks for it.
 */
function OverlayLink({ token }: { token: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <span className="meta-caps text-ink">Stream graphics</span>

      <p className="font-mono text-[11px] leading-relaxed text-soft">
        now playing, next in queue and recently played — as browser sources for
        OBS, Streamlabs or TikTok Studio, on a transparent background
      </p>

      <button
        type="button"
        data-overlay-open-dialog
        onClick={() => setOpen(true)}
        className="mt-1 flex items-center justify-center gap-2 rounded-[var(--radius-control)] border border-line px-3 py-2.5 text-sm font-medium text-ink transition-colors hover:border-line-strong hover:bg-raised"
      >
        <svg
          viewBox="0 0 16 16"
          className="h-4 w-4 text-soft"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <rect x="1.5" y="2.5" width="13" height="9" rx="1.5" />
          <path d="M5.5 14h5" />
        </svg>
        Set up overlays
      </button>

      {open && <OverlayDialog token={token} onClose={() => setOpen(false)} />}
    </div>
  );
}

/**
 * Whether this room takes tracks marked explicit.
 *
 * Beside the submissions policy because it is the same kind of decision - who
 * and what gets in - and a host setting one has usually just thought about the
 * other.
 *
 * On by default. A room that silently refused submissions because its owner
 * never opened a setting would be enforcing a rule nobody chose; turning it off
 * is the deliberate act, which is the right way round for a filter.
 */
function ExplicitSetting({
  room,
  onChange,
}: {
  room: Room;
  onChange: () => void;
}) {
  const update = useMutation({
    mutationFn: (accepts: boolean) =>
      rooms.settings({ accepts_explicit: accepts }),
    onSuccess: onChange,
  });

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <div className="flex items-center justify-between gap-3">
        <span className="meta-caps text-ink">Explicit tracks</span>

        <button
          type="button"
          role="switch"
          aria-checked={room.accepts_explicit}
          aria-label="Accept explicit tracks"
          data-accepts-explicit
          disabled={update.isPending}
          onClick={() => update.mutate(!room.accepts_explicit)}
          className={`h-6 w-11 shrink-0 rounded-[var(--radius-pill)] border transition-colors ${
            room.accepts_explicit
              ? "border-transparent bg-accent"
              : "border-line bg-raised"
          }`}
        >
          <span
            aria-hidden
            className={`block h-4 w-4 rounded-full bg-ink transition-transform ${
              room.accepts_explicit ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <p className="font-mono text-[11px] leading-relaxed text-soft">
        {room.accepts_explicit
          ? "Anything goes. Tracks the artist marked explicit can be submitted."
          : "Refused at the door, and the submitter is told why. This applies to your own crate too."}
      </p>
    </div>
  );
}

/**
 * Auto-refill: where the queue tops itself up from, and when.
 *
 * ## Why a host wants this
 *
 * The queue empties and the deck goes silent while the room is still live. That
 * arrives at the moment a host is busiest, and it arrives hardest for the hosts
 * with the smallest audiences - the ones with the fewest submissions to draw
 * on, who can least afford dead air.
 *
 * ## Off by default, and it says what it will actually do
 *
 * This puts music nobody submitted into somebody's broadcast: a good feature
 * and a bad surprise. So it starts off, and the line under the control
 * describes what will happen rather than naming the setting - "tracks from
 * across the platform, favouring artists who have not been heard much" tells a
 * host what they are agreeing to in a way that "Random" does not.
 *
 * ## The playlist pool is not saved until there is a playlist
 *
 * Choosing it opens a second select and changes nothing yet. Saving on the
 * first click would leave the room set to refill from a playlist it does not
 * have, which is a room that has auto-refill switched on and silently does
 * nothing - the worst of the three states to be in and the hardest to notice.
 */
function RefillSetting({
  room,
  onChange,
}: {
  room: Room;
  onChange: () => void;
}) {
  /*
   * Local, because "playlist" is a choice in progress until one is picked. The
   * saved value is still what everything else reads.
   */
  const [pool, setPool] = useState<string>(room.refill_pool ?? "off");

  const update = useMutation({
    mutationFn: (fields: Record<string, unknown>) => rooms.settings(fields),
    onSuccess: onChange,
  });

  /*
   * Only fetched once a playlist is the choice. Every host opens this dialog
   * and almost none pick this pool; loading their library to fill a select
   * nobody opened is a request per visit for nothing.
   */
  const playlists = useQuery({
    queryKey: ["my-playlists", room.username],
    queryFn: () =>
      api.get<{ items: { id: number; title: string }[] }>(
        `/users/${room.username}/collections?type=playlist`,
      ),
    enabled: pool === "playlist",
    staleTime: 60_000,
  });

  const DESCRIPTION: Record<string, string> = {
    off: "The queue is whatever people submit. If it empties, the room goes quiet.",
    random:
      "Tops up with tracks from across the platform, favouring artists who have not been heard much.",
    mine: "Tops up with your own public tracks.",
    playlist: "Plays through one of your playlists, in the order you built it.",
  };

  const choose = (next: string) => {
    setPool(next);

    // See the note above: the playlist pool waits for a playlist.
    if (next === "playlist" && !room.refill_playlist_id) return;

    update.mutate(
      next === "playlist"
        ? { refill_pool: next, refill_playlist_id: room.refill_playlist_id }
        : { refill_pool: next },
    );
  };

  return (
    <div className="flex flex-col gap-3 border-t border-line pt-5">
      <span className="meta-caps text-ink">Auto-refill</span>

      <Select
        value={pool}
        aria-label="Auto-refill source"
        data-refill-pool
        disabled={update.isPending}
        options={[
          { value: "off", label: "Off" },
          { value: "random", label: "Anything on the platform" },
          { value: "mine", label: "My tracks" },
          { value: "playlist", label: "One of my playlists" },
        ]}
        onChange={choose}
      />

      {pool === "playlist" && (
        <Select
          value={room.refill_playlist_id ? String(room.refill_playlist_id) : ""}
          aria-label="Which playlist"
          data-refill-playlist
          placeholder={playlists.isPending ? "Loading…" : "Choose a playlist"}
          disabled={update.isPending || playlists.isPending}
          options={(playlists.data?.items ?? []).map((item) => ({
            value: String(item.id),
            label: item.title,
          }))}
          onChange={(id) =>
            update.mutate({ refill_pool: "playlist", refill_playlist_id: Number(id) })
          }
        />
      )}

      {pool !== "off" && (
        <label className="flex items-center gap-2">
          <span className="font-mono text-[11px] text-soft">
            Top up when fewer than
          </span>
          {/*
            Saved on blur rather than on every keystroke: typing "12" over a "3"
            passes through "1", and a request per digit would set the room to
            something the host never chose.
          */}
          <input
            type="number"
            min={1}
            max={20}
            defaultValue={room.refill_below ?? 3}
            data-refill-below
            aria-label="Refill when fewer than this many are waiting"
            onBlur={(event) => {
              const value = Number(event.target.value);

              if (Number.isFinite(value) && value !== room.refill_below) {
                update.mutate({ refill_below: value });
              }
            }}
            className="w-14 rounded-[var(--radius-control)] border border-line bg-raised px-2 py-1 text-right text-sm text-ink"
          />
          <span className="font-mono text-[11px] text-soft">are waiting</span>
        </label>
      )}

      <p className="font-mono text-[11px] leading-relaxed text-soft">
        {DESCRIPTION[pool]}
      </p>
    </div>
  );
}

/**
 * A number a host sets, or turns off.
 *
 * Both fairness limits are the same control with different words, and the
 * words are what matters: each says what happens at the value chosen rather
 * than naming the setting, because "2" means nothing without the sentence
 * around it.
 */
function LimitSetting({
  label,
  hint,
  value,
  min,
  max,
  offLabel,
  describe,
  attr,
  onSave,
  saving,
}: {
  label: string;
  hint: string;
  value: number;
  min: number;
  max: number;
  offLabel: string;
  describe: (value: number) => string;
  attr: string;
  onSave: (value: number) => void;
  saving: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <div className="flex items-center justify-between gap-3">
        <span className="meta-caps text-ink">{label}</span>

        <select
          value={value}
          disabled={saving}
          aria-label={label}
          data-limit={attr}
          onChange={(event) => onSave(Number(event.target.value))}
          className="rounded-[var(--radius-control)] border border-line bg-raised px-2 py-1 text-sm text-ink"
        >
          <option value={0}>{offLabel}</option>
          {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>

      <p className="font-mono text-[11px] leading-relaxed text-soft">
        {value === 0 ? hint : describe(value)}
      </p>
    </div>
  );
}

/**
 * The two limits that keep a queue worth submitting to.
 *
 * ## They are a pair, and sit together on purpose
 *
 * A per-person cap pushes somebody with several tracks toward paying to get
 * more of them in. That is a reasonable thing for it to do and it is exactly
 * why the second control has to be next to it: a room that limits free
 * submissions and then never plays the ones it accepted has not been made
 * fairer, it has been made into a toll gate.
 *
 * ## Why the second one is not optional in spirit
 *
 * A free submission is worth at most 1.8 - one, plus half for a first-time
 * artist, plus three tenths for being in the room. The cheapest boost is 3.0.
 * So in any room where money is moving, no free submission ever reaches the
 * front on its own. Both default to off because turning them on changes a live
 * room under people, but a host running a busy paid queue with this off is
 * running a queue where free submissions are decoration.
 */
function FairnessSettings({
  room,
  onChange,
}: {
  room: Room;
  onChange: () => void;
}) {
  const update = useMutation({
    mutationFn: (fields: Record<string, number>) => rooms.settings(fields),
    onSuccess: onChange,
  });

  return (
    <>
      <LimitSetting
        label="Free tracks per person"
        attr="slots"
        hint="Anybody can hold as many free submissions in the queue as they like."
        value={room.free_slots_per_person ?? 0}
        min={1}
        max={10}
        offLabel="No limit"
        describe={(n) =>
          n === 1
            ? 'One free track each, waiting at a time. A slot opens up as soon as it plays.'
            : `${n} free tracks each, waiting at a time. A slot opens up each time one plays.`
        }
        saving={update.isPending}
        onSave={(free_slots_per_person) => update.mutate({ free_slots_per_person })}
      />

      <LimitSetting
        label="Guarantee free submissions"
        attr="every"
        /*
         * The off state names the consequence rather than the absence, because
         * the consequence is not obvious and is not neutral - see the class
         * note above.
         */
        hint="Boosted tracks always play first, so free submissions may never come up."
        value={room.free_every ?? 0}
        min={2}
        max={10}
        offLabel="Off"
        describe={(n) => `Every ${ordinal(n)} track is a free submission, if one is waiting.`}
        saving={update.isPending}
        onSave={(free_every) => update.mutate({ free_every })}
      />
    </>
  );
}

/** "3rd", for a sentence a host reads rather than a number they parse. */
function ordinal(n: number): string {
  const suffix = n % 10 === 1 && n % 100 !== 11
    ? 'st'
    : n % 10 === 2 && n % 100 !== 12
      ? 'nd'
      : n % 10 === 3 && n % 100 !== 13
        ? 'rd'
        : 'th';

  return `${n}${suffix}`;
}
